import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './SubagentsSkeleton.css';

/** Catalogue rows drawn under the toolbar. */
const ROWS = [0, 1, 2, 3];
/** The toolbar's selects: skills, voices and campaigns (the last one wider). */
const FILTERS = ['', '', ' subagents-skeleton__filter--wide'];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`subagents-skeleton__bone ${className}`} />
);

export interface SubagentsSkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of the Subagents catalogue (VOX-389). One shape from a cold load
 * to data: `RouteSkeleton` (inside the app chrome), the agent layouts while the
 * agent loads, the route's Suspense fallback and the page while its catalogue
 * loads all draw this.
 *
 * The page as it arrives (`SubagentsPage`, prototype `.sap`): breadcrumb, then
 * the card — title with New subagent beside it, the search and its three
 * filters, and the rows, each with its name, skill and voice, the record on the
 * right and its controls.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it. No
 * `aria-busy`: on a live region it tells assistive tech to hold the
 * announcement.
 */
export const SubagentsSkeleton: React.FC<SubagentsSkeletonProps> = ({ standalone = false }) => {
  const { t } = useTranslation(NS);
  const text = t('loading');

  return (
    <div
      className={`subagents-skeleton${standalone ? ' subagents-skeleton--standalone' : ''}`}
      role="status"
      data-testid="subagents-skeleton"
    >
      <span className="subagents-skeleton__sr-only">{text}</span>
      <div className="subagents-skeleton__header" aria-hidden="true">
        <Bone className="subagents-skeleton__crumb" />
      </div>
      <div className="subagents-skeleton__card" aria-hidden="true">
        <div className="subagents-skeleton__head">
          <Bone className="subagents-skeleton__title" />
          <Bone className="subagents-skeleton__button" />
        </div>
        <div className="subagents-skeleton__toolbar">
          <Bone className="subagents-skeleton__search" />
          {FILTERS.map((modifier, index) => (
            <Bone key={index} className={`subagents-skeleton__filter${modifier}`} />
          ))}
        </div>
        <div className="subagents-skeleton__list">
          {ROWS.map((row) => (
            <div key={row} className="subagents-skeleton__row">
              <div className="subagents-skeleton__id">
                <Bone className="subagents-skeleton__name" />
                <div className="subagents-skeleton__specs">
                  <Bone className="subagents-skeleton__spec" />
                  <Bone className="subagents-skeleton__spec" />
                </div>
              </div>
              <div className="subagents-skeleton__record">
                <Bone className="subagents-skeleton__record-number" />
                <Bone className="subagents-skeleton__record-label" />
              </div>
              <div className="subagents-skeleton__actions">
                <Bone className="subagents-skeleton__icon" />
                <Bone className="subagents-skeleton__icon" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
